import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CargoPackagingProps,
  CargoPackagingResponse,
  RequestWmsParams,
} from "./types";
import { exportLiteWmsApi, getWmsListApi, updatePackingListApi } from "./api";
import { AppThunk } from "../../store";
import {
  getErrorMessageFromBlobResponse,
  getErrorMessageFromResponse,
} from "@helpers/get-error-message";

type LiteWmsType = CargoPackagingResponse & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestWmsParams> | null;
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: LiteWmsType = {
  items: null,
  meta: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: {
    loading: false,
    error: null,
  },
};

const liteWms = createSlice({
  name: "liteWms",
  initialState: initialState,
  reducers: {
    getWmsStart(state: LiteWmsType) {
      state.loading = true;
      state.error = null;
    },
    getWmsSuccess(
      state: LiteWmsType,
      action: PayloadAction<
        CargoPackagingResponse & { params?: RequestWmsParams }
      >,
    ) {
      const { items, total, page, size, meta, params } = action.payload;
      state.items = items;
      state.meta = meta;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getWmsFailure(state: LiteWmsType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.items = null;
    },
    getExportFailure(state: LiteWmsType, action: PayloadAction<string>) {
      state.error = action.payload;
      state.exporting.loading = false;
    },
    hideWarning(state: LiteWmsType) {
      state.error = null;
      state.success = null;
    },
    updateWmsItem(
      state: LiteWmsType,
      action: PayloadAction<{ id: number; data: Partial<CargoPackagingProps> }>,
    ) {
      const { id, data } = action.payload;
      if (!state.items) return;

      const idx = state.items.findIndex((item) => item.id === id);
      if (idx !== -1) {
        state.items[idx] = {
          ...state.items[idx],
          ...data,
        };
      }
    },
    updateWmsSuccess(state: LiteWmsType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = null;
      state.success = action.payload;
    },
    updateWmsFailure(state: LiteWmsType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    cleanPackage(state: LiteWmsType) {
      state.items = null;
    },
    startExport(state: LiteWmsType) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: LiteWmsType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getWmsStart,
  hideWarning,
  cleanPackage,
  getWmsSuccess,
  getWmsFailure,
  getExportFailure,
  startExport,
  successExport,
  updateWmsItem,
  updateWmsSuccess,
  updateWmsFailure,
} = liteWms.actions;

export default liteWms.reducer;

export const fetchWmsList =
  (params: RequestWmsParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getWmsStart());
      const data = await getWmsListApi(params);
      dispatch(getWmsSuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getWmsFailure(error));
    }
  };

export const updateLiteWmsPackingList =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CargoPackagingProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getWmsStart());
      const data = await updatePackingListApi(id, params);
      dispatch(
        updateWmsItem({ id, data: { ...data, warehouse: params.warehouse } }),
      );
      dispatch(updateWmsSuccess("Packing list updated successfully"));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(updateWmsFailure(error));
    }
  };

export const exportLiteWms =
  (params?: Partial<RequestWmsParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportLiteWmsApi(params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = await getErrorMessageFromBlobResponse(err);
      dispatch(getExportFailure(error));
    }
  };
